<template>
  <div class="alice-shell theme-turquoise">
    <TopBar class="alice-shell__topbar">
      <template #breadcrumb>
        <router-link to="/skills" class="alice-tb-crumb">Skills</router-link>
        <span class="alice-tb-sep">›</span>
        <span class="alice-tb-crumb alice-tb-crumb--current">{{
          current ? current.title || current.name || "New skill" : "Not found"
        }}</span>
      </template>
    </TopBar>

    <Sidebar class="alice-shell__sidebar" variant="rail" active-key="skills" />

    <!-- Editor -->
    <main class="alice-shell__main alice-skills__main">
      <div v-if="loading" class="alice-skills__loading">
        <span class="alice-skills__spinner alice-skills__spinner--lg" />
      </div>
      <div v-else-if="!current" class="alice-skills__empty">
        This skill doesn't exist (any more).
        <router-link to="/skills">Back to all skills</router-link>
      </div>

      <div v-else class="alice-skills__editor">
        <header class="alice-skills__editor-head">
          <div class="alice-skills__editor-heading">
            <h1 class="alice-skills__editor-title">
              {{ current.title || current.name || "New skill" }}
            </h1>
            <div class="alice-skills__editor-meta">
              <span
                v-for="c in skillChips(current)"
                :key="c.kind"
                class="alice-skills__state"
                :class="`alice-skills__state--${c.kind}`"
                :title="c.title"
                >{{ c.label }}</span
              >
            </div>
          </div>
          <div class="alice-skills__editor-actions">
            <button
              class="alice-skills__ghost alice-skills__ghost--icon"
              :class="{ 'alice-skills__ghost--on': showChat }"
              :aria-pressed="showChat"
              @click="showChat = !showChat"
            >
              <Icon name="star" :size="14" /><span>Edit with AI</span>
            </button>
            <button
              v-if="current.status === 'published' && canApprove"
              class="alice-skills__ghost"
              :disabled="acting"
              title="Take this skill offline (stops serving to chat & MCP)"
              @click="unpublish"
            >
              Unpublish
            </button>
            <button
              class="alice-skills__delete"
              title="Delete skill"
              @click="removeSkill"
            >
              <Icon name="trash" :size="14" />
            </button>
            <button class="alice-skills__save" :disabled="saving" @click="save">
              <span v-if="saving" class="alice-skills__spinner" />
              <span>{{ saving ? "Saving…" : "Save & send for review" }}</span>
            </button>
          </div>
        </header>

        <nav class="alice-skills__tabs">
          <button
            v-for="t in tabs"
            :key="t.key"
            class="alice-skills__tab"
            :class="{ 'alice-skills__tab--active': tab === t.key }"
            :disabled="t.needsSaved && !current.id"
            @click="tab = t.key"
          >
            {{ t.label
            }}<span v-if="t.count != null" class="alice-skills__tab-count">{{
              t.count
            }}</span>
          </button>
        </nav>

        <!-- EDIT -->
        <div v-show="tab === 'edit'" class="alice-skills__fields">
          <div
            v-if="pendingFeedbackIds.length"
            class="alice-skills__applied"
            role="status"
          >
            {{
              pendingFeedbackIds.length === 1
                ? "Feedback applied"
                : `${pendingFeedbackIds.length} feedback items applied`
            }}
            in the editor. Save &amp; send for review to put
            {{ pendingFeedbackIds.length === 1 ? "it" : "them" }} in a new
            version; the feedback is then marked as applied.
          </div>
          <label class="alice-skills__field">
            <span class="alice-skills__label">Title</span>
            <input
              v-model="current.title"
              type="text"
              placeholder="Human-friendly name (e.g. Review UX research report)"
            />
          </label>

          <label class="alice-skills__field">
            <span class="alice-skills__label"
              >Category <em>(library grouping)</em></span
            >
            <input
              v-model="current.category"
              type="text"
              list="alice-skill-categories"
              placeholder="UX Research"
              @change="saveCategory"
            />
            <datalist id="alice-skill-categories">
              <option v-for="c in categorySuggestions" :key="c" :value="c" />
            </datalist>
            <span class="alice-skills__field-hint">
              <template v-if="categorySaveState === 'saving'">
                <span class="alice-skills__spinner alice-skills__spinner--sm" />
                Saving…
              </template>
              <template v-else-if="categorySaveState === 'saved'"
                >Saved ✓</template
              >
              <template v-else
                >Saved automatically — no review needed.</template
              >
            </span>
          </label>

          <label class="alice-skills__field">
            <span class="alice-skills__label"
              >Name <em>(kebab-case identifier)</em></span
            >
            <input
              v-model="current.name"
              type="text"
              placeholder="review-ux-research-report"
              spellcheck="false"
            />
          </label>

          <label class="alice-skills__field">
            <span class="alice-skills__label"
              >Description
              <em
                >(the trigger: what it does + “Use whenever [situation]…”)</em
              ></span
            >
            <textarea
              v-model="current.description"
              rows="3"
              placeholder="Review a draft UX research report for clarity, evidence and structure. Use whenever a colleague shares a report draft or asks for feedback on findings."
            />
          </label>

          <SkillBodyEditor v-model="current.body" />

          <div
            v-if="validation.errors.length || validation.warnings.length"
            class="alice-skills__validation"
          >
            <div
              v-for="(e, i) in validation.errors"
              :key="`e${i}`"
              class="alice-skills__v alice-skills__v--error"
            >
              <Icon name="lock" :size="12" /><span>{{ e }}</span>
            </div>
            <div
              v-for="(w, i) in validation.warnings"
              :key="`w${i}`"
              class="alice-skills__v alice-skills__v--warn"
            >
              <Icon name="star" :size="12" /><span>{{ w }}</span>
            </div>
          </div>
          <p class="alice-skills__hint">
            Saving creates a new version and sends it for review. The live
            version keeps serving chat &amp; MCP until someone approves the new
            one.
          </p>
        </div>

        <!-- USAGE -->
        <div v-show="tab === 'usage'" class="alice-skills__pane">
          <div v-if="!currentUsage" class="alice-skills__pane-empty">
            Not used yet. A use is counted each time this skill is active in an
            Alice answer (chat or team chat) or fetched by an external MCP
            client.
          </div>
          <template v-else>
            <div class="alice-skills__usage-kpis">
              <div class="alice-skills__usage-kpi">
                <span class="alice-skills__usage-kpi-value">{{
                  currentUsage.uses || 0
                }}</span>
                <span class="alice-skills__usage-kpi-label"
                  >Uses, all time</span
                >
              </div>
              <div class="alice-skills__usage-kpi">
                <span class="alice-skills__usage-kpi-value">{{
                  usageLast30
                }}</span>
                <span class="alice-skills__usage-kpi-label">Last 30 days</span>
              </div>
              <div
                class="alice-skills__usage-kpi"
                title="Times Alice loaded this skill on its own because the request matched its description"
              >
                <span class="alice-skills__usage-kpi-value">{{
                  currentUsage.autoLoads || 0
                }}</span>
                <span class="alice-skills__usage-kpi-label"
                  >Picked by Alice</span
                >
              </div>
              <div class="alice-skills__usage-kpi">
                <span
                  class="alice-skills__usage-kpi-value alice-skills__usage-kpi-value--sm"
                  :title="fmtDate(currentUsage.lastUsedAt)"
                  >{{ fmtRelative(currentUsage.lastUsedAt) }}</span
                >
                <span class="alice-skills__usage-kpi-label">Last used</span>
              </div>
            </div>

            <div class="alice-skills__usage-sources">
              <span v-for="s in usageSources" :key="s.key"
                ><strong>{{ s.count }}</strong> {{ s.label }}</span
              >
            </div>

            <div>
              <div class="alice-skills__usage-trend-label">
                Uses per day · last 30 days
              </div>
              <div
                class="alice-skills__usage-trend"
                role="img"
                aria-label="Uses per day over the last 30 days"
              >
                <span
                  v-for="d in usageTrend"
                  :key="d.date"
                  class="alice-skills__usage-bar"
                  :title="`${d.date} · ${d.uses} ${
                    d.uses === 1 ? 'use' : 'uses'
                  }`"
                >
                  <span
                    class="alice-skills__usage-fill"
                    :style="{ height: `${trendHeight(d.uses)}%` }"
                  />
                </span>
              </div>
            </div>

            <p class="alice-skills__hint">
              A use is one answer this skill was active in — pinned by a
              teammate, sticky from an earlier turn, or picked by Alice — or one
              fetch by an external MCP client such as Claude or Codex.
            </p>
          </template>
        </div>

        <!-- VERSIONS -->
        <div v-show="tab === 'versions'" class="alice-skills__pane">
          <div v-if="!versions.length" class="alice-skills__pane-empty">
            No versions yet.
          </div>
          <ul class="alice-skills__versions">
            <li
              v-for="ver in versions"
              :key="ver.id"
              class="alice-skills__version"
            >
              <div class="alice-skills__version-main">
                <span class="alice-skills__vtag">v{{ ver.version }}</span>
                <span
                  v-if="ver.version === current.currentVersion"
                  class="alice-skills__version-current"
                  >current</span
                >
                <span class="alice-skills__version-msg">{{
                  ver.message || ver.title || ver.name
                }}</span>
              </div>
              <div class="alice-skills__version-sub">
                {{ userName(ver.authorUid) }} · {{ fmtDate(ver.createdAt) }}
              </div>
              <div class="alice-skills__version-actions">
                <button
                  class="alice-skills__linkbtn"
                  @click="previewVersion = ver"
                >
                  Preview
                </button>
                <button
                  v-if="ver.version !== current.currentVersion"
                  class="alice-skills__linkbtn"
                  :disabled="acting"
                  @click="revert(ver)"
                >
                  Restore
                </button>
              </div>
            </li>
          </ul>

          <div v-if="previewVersion" class="alice-skills__preview">
            <div class="alice-skills__preview-head">
              <span>Preview — v{{ previewVersion.version }}</span>
              <button
                class="alice-skills__linkbtn"
                @click="previewVersion = null"
              >
                Close
              </button>
            </div>
            <pre class="alice-skills__preview-body">{{
              previewVersion.body
            }}</pre>
          </div>
        </div>

        <!-- APPROVAL -->
        <div v-show="tab === 'reviewers'" class="alice-skills__pane">
          <p class="alice-skills__hint">
            Max, the owner, or any approver can approve. Approving publishes the
            version immediately.
          </p>

          <!-- Decisions already made on the version in review -->
          <div v-if="currentDecisions.length">
            <div class="alice-skills__section-title">
              Decisions on v{{ current.currentVersion || current.version }}
            </div>
            <div class="alice-skills__reviewers">
              <div
                v-for="r in currentDecisions"
                :key="r.uid"
                class="alice-skills__reviewer"
              >
                <span>{{ r.name }}</span>
                <span
                  class="alice-skills__decision"
                  :class="`alice-skills__decision--${r.decision}`"
                  >{{ decisionLabel(r.decision) }}</span
                >
              </div>
            </div>
          </div>

          <!-- Owner: single, editable only by Max -->
          <div class="alice-skills__section-title">Owner</div>
          <div class="alice-skills__chips">
            <span v-if="current.owner" class="alice-skills__chip">{{
              userName(current.owner)
            }}</span>
            <span v-else class="alice-skills__pane-empty">No owner set.</span>
          </div>
          <div v-if="isMax" class="alice-skills__inline-edit">
            <select v-model="ownerSelection" class="alice-skills__select">
              <option :value="null">— none —</option>
              <option v-for="u in users" :key="u.uid" :value="u.uid">
                {{ u.name || u.email || u.uid }}
              </option>
            </select>
            <button
              class="alice-skills__save alice-skills__save--sm"
              :disabled="acting"
              @click="saveOwner"
            >
              Save owner
            </button>
          </div>

          <!-- Approvers: global list, any one can approve. Max-only edit -->
          <div class="alice-skills__section-title">
            Approvers <em>(global — set by Max)</em>
          </div>
          <div class="alice-skills__chips">
            <span
              v-for="uid in approvers"
              :key="uid"
              class="alice-skills__chip"
              >{{ userName(uid) }}</span
            >
            <span v-if="!approvers.length" class="alice-skills__pane-empty"
              >No extra approvers — Max and the owner can approve.</span
            >
          </div>
          <details v-if="isMax" class="alice-skills__assign">
            <summary>Edit approvers</summary>
            <div class="alice-skills__assign-list">
              <label
                v-for="u in users"
                :key="u.uid"
                class="alice-skills__assign-row"
              >
                <input
                  type="checkbox"
                  :value="u.uid"
                  v-model="approverSelection"
                />
                <span>{{ u.name || u.email || u.uid }}</span>
              </label>
            </div>
            <button
              class="alice-skills__save alice-skills__save--sm"
              :disabled="savingApprovers"
              @click="saveApprovers"
            >
              Save approvers
            </button>
          </details>

          <div v-if="showReviewBox" class="alice-skills__review-box">
            <div class="alice-skills__section-title">Your review</div>
            <textarea
              v-model="reviewComment"
              rows="2"
              placeholder="Optional comment…"
            />
            <div class="alice-skills__review-actions">
              <button
                class="alice-skills__approve"
                :disabled="acting"
                @click="review('approved')"
              >
                Approve &amp; publish
              </button>
              <button
                class="alice-skills__request"
                :disabled="acting"
                @click="review('changes_requested')"
              >
                Request changes
              </button>
            </div>
          </div>
        </div>

        <!-- COMMENTS -->
        <div v-show="tab === 'comments'" class="alice-skills__pane">
          <div v-if="!comments.length" class="alice-skills__pane-empty">
            No comments yet.
          </div>
          <ul class="alice-skills__comments">
            <li
              v-for="c in comments"
              :key="c.id"
              class="alice-skills__comment"
              :class="{ 'alice-skills__comment--resolved': c.resolved }"
            >
              <div class="alice-skills__comment-head">
                <span class="alice-skills__comment-author">{{
                  userName(c.authorUid)
                }}</span>
                <span class="alice-skills__comment-date">{{
                  fmtDate(c.createdAt)
                }}</span>
                <button class="alice-skills__linkbtn" @click="toggleResolve(c)">
                  {{ c.resolved ? "Reopen" : "Resolve" }}
                </button>
              </div>
              <div class="alice-skills__comment-body">{{ c.text }}</div>
            </li>
          </ul>
          <form class="alice-skills__comment-form" @submit.prevent="addComment">
            <textarea
              v-model="newComment"
              rows="2"
              placeholder="Add a comment…"
            />
            <button
              class="alice-skills__save alice-skills__save--sm"
              :disabled="!newComment.trim()"
            >
              Comment
            </button>
          </form>
        </div>

        <!-- FEEDBACK -->
        <div v-show="tab === 'feedback'" class="alice-skills__pane">
          <div class="alice-skills__fb-intro">
            <p class="alice-skills__hint">
              What colleagues sent while using this skill, from Claude or
              ChatGPT (Valsplat MCP) or from Alice's chat. Nothing here changes
              the live skill: apply a proposal or process the feedback with AI,
              then save &amp; send for review.
            </p>
            <button
              v-if="canApprove && feedbackSplit.open.length"
              class="alice-skills__ghost alice-skills__ghost--icon"
              @click="processFeedbackWithAI"
            >
              <Icon name="star" :size="14" /><span>Process with AI</span>
            </button>
          </div>
          <div v-if="!feedback.length" class="alice-skills__pane-empty">
            No feedback yet. It comes in when someone corrects output that
            followed this skill, answers Alice's “did this skill work?”, or asks
            for a change.
          </div>
          <div
            v-else-if="!feedbackSplit.open.length"
            class="alice-skills__pane-empty"
          >
            No open feedback.
          </div>
          <ul class="alice-skills__fbs">
            <li
              v-for="f in feedbackShown"
              :key="f.id"
              class="alice-skills__fb"
              :class="{ 'alice-skills__fb--handled': f.status !== 'open' }"
            >
              <div class="alice-skills__fb-head">
                <span
                  class="alice-skills__fb-kind"
                  :class="`alice-skills__fb-kind--${f.kind}`"
                  >{{ kindLabels[f.kind] || f.kind }}</span
                >
                <span
                  v-if="f.rating"
                  class="alice-skills__fb-rating"
                  :class="`alice-skills__fb-rating--${f.rating}`"
                  >{{ ratingLabels[f.rating] || f.rating }}</span
                >
                <span
                  v-if="f.status !== 'open'"
                  class="alice-skills__fb-resolution"
                  >{{ resolutionLabel(f) }}</span
                >
              </div>
              <div class="alice-skills__fb-summary">{{ f.summary }}</div>
              <div v-if="f.details" class="alice-skills__fb-details">
                {{ f.details }}
              </div>
              <div class="alice-skills__fb-meta">
                {{ feedbackAuthor(f) }} ·
                {{ sourceLabels[f.source] || f.source }} ·
                {{ fmtDate(f.createdAt)
                }}<template v-if="f.version"> · on v{{ f.version }}</template>
              </div>
              <div v-if="f.proposed" class="alice-skills__fb-proposal">
                <button
                  class="alice-skills__linkbtn"
                  :aria-expanded="!!diffOpen[f.id]"
                  @click="diffOpen = { ...diffOpen, [f.id]: !diffOpen[f.id] }"
                >
                  {{ diffOpen[f.id] ? "Hide changes" : "Show changes to live" }}
                </button>
                <div v-if="diffOpen[f.id]" class="alice-skills__diff">
                  <div
                    v-if="f.proposed.description"
                    class="alice-skills__diff-desc"
                  >
                    <strong>New description:</strong>
                    {{ f.proposed.description }}
                  </div>
                  <div
                    v-for="(row, i) in proposalDiff(f)"
                    :key="i"
                    class="alice-skills__diff-row"
                    :class="`alice-skills__diff-row--${row.type}`"
                  >
                    {{
                      row.type === "skip"
                        ? `… ${row.count} unchanged lines`
                        : row.text || " "
                    }}
                  </div>
                </div>
              </div>
              <div v-if="canApprove" class="alice-skills__fb-actions">
                <template v-if="f.status === 'open'">
                  <button
                    v-if="f.proposed"
                    class="alice-skills__linkbtn"
                    @click="applyProposal(f)"
                  >
                    Apply to editor
                  </button>
                  <button
                    class="alice-skills__linkbtn"
                    :disabled="acting"
                    @click="resolveFeedback([f.id], 'done')"
                  >
                    Mark handled
                  </button>
                  <button
                    class="alice-skills__linkbtn"
                    :disabled="acting"
                    @click="resolveFeedback([f.id], 'declined')"
                  >
                    Decline
                  </button>
                </template>
                <button
                  v-else
                  class="alice-skills__linkbtn"
                  :disabled="acting"
                  @click="resolveFeedback([f.id], 'open')"
                >
                  Reopen
                </button>
              </div>
            </li>
          </ul>
          <button
            v-if="feedbackSplit.handled.length"
            class="alice-skills__linkbtn alice-skills__fb-more"
            @click="showHandled = !showHandled"
          >
            {{ showHandled ? "Hide" : "Show" }} handled feedback ({{
              feedbackSplit.handled.length
            }})
          </button>
        </div>
      </div>
    </main>

    <!-- AI authoring drawer -->
    <div v-if="showChat" class="alice-skills__drawer">
      <SkillAuthoringChat
        :draft="current || null"
        :seed="aiSeed"
        @save="onAuthored"
        @close="showChat = false"
      />
    </div>
  </div>
</template>

<script>
import { mapState } from "vuex";
import {
  collection,
  addDoc,
  doc,
  updateDoc,
  deleteDoc,
  getDoc,
  getDocs,
  query,
  orderBy,
  where,
  onSnapshot,
} from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { db, functions } from "@/firebase";

import TopBar from "@/components/alice/TopBar.vue";
import Sidebar from "@/components/alice/Sidebar.vue";
import Icon from "@/components/alice/Icon.vue";
import SkillAuthoringChat from "@/components/alice/SkillAuthoringChat.vue";
import SkillBodyEditor from "@/components/alice/SkillBodyEditor.vue";
import { cleanCategory } from "@/utils/skillLibrary";
import {
  KIND_LABELS,
  RATING_LABELS,
  SOURCE_LABELS,
  splitFeedback,
  resolutionLabel,
  lineDiff,
  foldDiff,
  feedbackPrompt,
} from "@/utils/skillFeedback";

const KEBAB_RE = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
// Content fields the Save button commits; category autosaves on its own.
const CONTENT_FIELDS = ["title", "name", "description", "body"];

// Client-side mirror of modules/skills/format.js validateSkill (the frontend
// can't require the Cloud Functions module). Keep the two in sync.
function validateSkill(skill = {}) {
  const errors = [];
  const warnings = [];
  const name = (skill.name || "").trim();
  const category = cleanCategory(skill.category);
  const description = (skill.description || "").trim();
  const body = (skill.body || "").trim();

  if (!name) errors.push("name is required.");
  else {
    if (name.length > 64) errors.push("name must be 64 characters or fewer.");
    if (!KEBAB_RE.test(name))
      errors.push(
        "name must be kebab-case (lowercase letters, digits and single hyphens)."
      );
  }
  if (category.length > 80)
    errors.push("category must be 80 characters or fewer.");
  if (!description) errors.push("description is required.");
  else {
    if (description.length < 20)
      warnings.push(
        "description is very short — describe what the skill does AND when Alice should use it."
      );
    if (!/\b(use|when|whenever|for)\b/i.test(description))
      warnings.push(
        'description should include a trigger, e.g. "Use when…" so Alice knows when to apply it.'
      );
  }
  if (!body) errors.push("body is required — write the skill instructions.");
  else if (body.length < 80)
    warnings.push(
      "body is very short — a good skill has a clear workflow, steps, and an example."
    );

  return { ok: errors.length === 0, errors, warnings };
}

export default {
  name: "SkillsPage",
  components: { TopBar, Sidebar, Icon, SkillAuthoringChat, SkillBodyEditor },
  data() {
    return {
      // Local working copies — content edits commit via saveSkillVersion.
      working: [],
      active: 0,
      // True while the initial skills fetch is in flight.
      loading: true,
      saving: false,
      acting: false,
      // Category autosave feedback: "idle" | "saving" | "saved" (transient).
      categorySaveState: "idle",
      categorySavedTimer: null,
      showChat: false,
      tab: "edit",
      // Set right before a navigation this page starts itself (after a delete),
      // so the unsaved-changes guard doesn't ask about a skill that's gone.
      skipLeaveGuard: false,
      // Review data for the selected skill.
      versions: [],
      comments: [],
      approvals: [],
      previewVersion: null,
      // Owner editor working copy (single uid; Max-only).
      ownerSelection: null,
      // Global approvers list (admin/skillApprovers) + its editor working copy.
      approvers: [],
      approverSelection: [],
      savingApprovers: false,
      reviewComment: "",
      newComment: "",
      // Feedback sent through update_skill (skills/{id}/feedback).
      feedback: [],
      showHandled: false,
      diffOpen: {},
      // Feedback applied in the editor (a proposal, or the AI drawer's
      // revision of it): marked "applied" once the next save succeeds.
      pendingFeedbackIds: [],
      // First message for the AI drawer ("Process with AI") and the items it
      // covers, taken over once the drawer's draft is applied.
      aiSeed: null,
      aiSeedIds: [],
      kindLabels: KIND_LABELS,
      ratingLabels: RATING_LABELS,
      sourceLabels: SOURCE_LABELS,
      // Skill usage counters (skillUsage/{skillId}), live via onSnapshot, and
      // the selected skill's last-30-day buckets.
      usageById: {},
      usageDaily: [],
      usageUnsub: null,
    };
  },
  computed: {
    ...mapState({
      skills: (state) => state.skills,
      user: (state) => state.user,
      users: (state) => state.users || [],
    }),
    current() {
      return this.working[this.active] || null;
    },
    currentUsage() {
      const id = this.current && this.current.id;
      return (id && this.usageById[id]) || null;
    },
    usageLast30() {
      return this.usageDaily.reduce((n, d) => n + (d.uses || 0), 0);
    },
    // One entry per UTC day for the last 30 days, zero-filled, oldest first.
    usageTrend() {
      const byDate = Object.fromEntries(
        this.usageDaily.map((d) => [d.date, d.uses || 0])
      );
      const days = [];
      for (let i = 29; i >= 0; i--) {
        const date = new Date(Date.now() - i * 86400000)
          .toISOString()
          .slice(0, 10);
        days.push({ date, uses: byDate[date] || 0 });
      }
      return days;
    },
    usageSources() {
      const src = (this.currentUsage && this.currentUsage.bySource) || {};
      return [
        { key: "chat", label: "in chat", count: src.chat || 0 },
        { key: "communal", label: "in team chat", count: src.communal || 0 },
        { key: "mcp", label: "via MCP", count: src.mcp || 0 },
      ];
    },
    categorySuggestions() {
      const byKey = new Map();
      this.working.forEach((skill) => {
        const label = cleanCategory(skill.category);
        if (label && !byKey.has(label.toLocaleLowerCase())) {
          byKey.set(label.toLocaleLowerCase(), label);
        }
      });
      return [...byKey.values()].sort((a, b) => a.localeCompare(b));
    },
    validation() {
      return this.current
        ? validateSkill(this.current)
        : { ok: true, errors: [], warnings: [] };
    },
    feedbackSplit() {
      return splitFeedback(this.feedback);
    },
    feedbackShown() {
      const { open, handled } = this.feedbackSplit;
      return this.showHandled ? [...open, ...handled] : open;
    },
    tabs() {
      return [
        { key: "edit", label: "Edit" },
        {
          key: "feedback",
          label: "Feedback",
          count: this.feedbackSplit.open.length || null,
          needsSaved: true,
        },
        {
          key: "versions",
          label: "Versions",
          count: this.versions.length || null,
          needsSaved: true,
        },
        {
          key: "usage",
          label: "Usage",
          count: (this.currentUsage && this.currentUsage.uses) || null,
          needsSaved: true,
        },
        {
          key: "reviewers",
          label: "Approval",
          needsSaved: true,
        },
        {
          key: "comments",
          label: "Comments",
          count: this.comments.length || null,
          needsSaved: true,
        },
      ];
    },
    isMax() {
      return !!(this.user && this.user.email === "max@braingineers.com");
    },
    // Mirrors the server's requireApprover: Max, the skill's owner, or anyone
    // on the global approvers list. The server re-checks, so this only drives
    // what the UI shows.
    canApprove() {
      if (!this.user) return false;
      return (
        this.isMax ||
        (this.current && this.current.owner === this.user.uid) ||
        this.approvers.includes(this.user.uid)
      );
    },
    showReviewBox() {
      if (!this.canApprove || !this.current || !this.current.id) return false;
      const s = this.current;
      if (["in_review", "changes_requested"].includes(s.reviewState)) {
        return true;
      }
      // Approved but offline (unpublished): approving brings it back live.
      return s.reviewState === "approved" && s.status !== "published";
    },
    // Latest decision per reviewer on the version currently in review.
    currentDecisions() {
      if (!this.current) return [];
      const cv = this.current.currentVersion || this.current.version || 1;
      const latest = {};
      this.approvals
        .filter((a) => a.version === cv)
        .forEach((a) => {
          const prev = latest[a.reviewerUid];
          if (!prev || (a.createdAt || 0) > (prev.createdAt || 0))
            latest[a.reviewerUid] = a;
        });
      return Object.values(latest).map((a) => ({
        uid: a.reviewerUid,
        name: this.userName(a.reviewerUid),
        decision: a.decision,
      }));
    },
  },
  async mounted() {
    this.loading = true;
    // Usage counters are small (one doc per skill) and change with every
    // answer, so keep them live rather than refetching per selection.
    this.usageUnsub = onSnapshot(
      collection(db, "skillUsage"),
      (snap) => {
        const byId = {};
        snap.docs.forEach((d) => {
          byId[d.id] = d.data();
        });
        this.usageById = byId;
      },
      (err) => console.warn("skillUsage subscription failed:", err)
    );
    window.addEventListener("beforeunload", this.onBeforeUnload);
    try {
      await this.loadApprovers();
      // The store may predate this skill (a colleague just made it, or the
      // link came from Slack): fetch again when the id isn't in it.
      const id = this.$route.params.id;
      const known = (this.skills || []).some((s) => s.id === id);
      if (!this.skills || !this.skills.length || (id !== "new" && !known)) {
        await this.$store.dispatch("fetchSkills");
      }
      this.syncFromStore();
      this.openFromRoute();
    } finally {
      this.loading = false;
    }
  },
  beforeUnmount() {
    window.removeEventListener("beforeunload", this.onBeforeUnload);
    if (this.categorySavedTimer) clearTimeout(this.categorySavedTimer);
    if (this.usageUnsub) this.usageUnsub();
  },
  // Edits live only in this page's working copy until Save, so leaving the
  // page (back to the library, another skill, a reload) asks first.
  beforeRouteLeave() {
    return this.confirmLeave();
  },
  beforeRouteUpdate(to, from) {
    if (to.params.id === from.params.id) return true;
    return this.confirmLeave();
  },
  watch: {
    "$route.params.id"(id) {
      if (id && !this.loading) this.openFromRoute();
    },
    // Closing the drawer drops a "Process with AI" hand-over that wasn't
    // applied, so reopening it starts a fresh conversation.
    showChat(open) {
      if (!open) {
        this.aiSeed = null;
        this.aiSeedIds = [];
      }
    },
  },
  methods: {
    // Select the skill the URL names; "new" stages (or reuses) an unsaved
    // draft, and ?ai=1 opens the AI drawer on it.
    openFromRoute() {
      const id = this.$route.params.id;
      if (id === "new") {
        const i = this.working.findIndex((s) => !s.id);
        if (i >= 0) this.selectSkill(i);
        else this.addSkill();
      } else {
        const i = this.working.findIndex((s) => s.id === id);
        if (i >= 0) this.selectSkill(i);
        else this.active = -1;
      }
      if (this.$route.query.ai) this.showChat = true;
      const tab = this.$route.query.tab;
      if (tab && this.current && this.current.id) {
        if (this.tabs.some((t) => t.key === tab)) this.tab = tab;
      }
    },
    // True when the working copy differs from what was last saved (or, for a
    // new draft, when anything was typed at all).
    isDirty(s) {
      if (!s) return false;
      if (!s.id) return CONTENT_FIELDS.some((k) => (s[k] || "").trim());
      const saved = (this.skills || []).find((x) => x.id === s.id);
      return (
        !!saved && CONTENT_FIELDS.some((k) => (s[k] || "") !== (saved[k] || ""))
      );
    },
    confirmLeave() {
      if (this.skipLeaveGuard || !this.isDirty(this.current)) return true;
      return window.confirm(
        "You have unsaved changes to this skill. Leave without saving?"
      );
    },
    onBeforeUnload(e) {
      if (!this.isDirty(this.current)) return;
      e.preventDefault();
      e.returnValue = "";
    },
    normalizeCurrentCategory() {
      if (!this.current) return;
      const value = cleanCategory(this.current.category);
      const match = this.working.find(
        (skill) =>
          skill !== this.current &&
          cleanCategory(skill.category).toLocaleLowerCase() ===
            value.toLocaleLowerCase()
      );
      const existing = match && cleanCategory(match.category);
      this.current.category = existing || value;
    },
    // Category is a library grouping only — it never feeds chat/MCP serving — so
    // it autosaves straight to the top-level doc, decoupled from the version +
    // review pipeline. No version bump, no reset to in_review. Unsaved drafts
    // (no id) get their category on the first full save().
    async saveCategory() {
      if (!this.current) return;
      this.normalizeCurrentCategory();
      const category = cleanCategory(this.current.category);
      if (!this.current.id) return;
      if (this.categorySavedTimer) clearTimeout(this.categorySavedTimer);
      this.categorySaveState = "saving";
      try {
        await updateDoc(doc(db, "skills", this.current.id), { category });
        this.categorySaveState = "saved";
        this.categorySavedTimer = setTimeout(() => {
          this.categorySaveState = "idle";
        }, 1600);
      } catch (e) {
        console.error("Category autosave failed:", e);
        this.categorySaveState = "idle";
      }
    },
    // The whole status in at most two chips: what's live, and what's pending.
    // "Live v1" alone means nothing is in flight; a second chip appears only
    // when a newer version is in the pipeline (or the skill isn't live yet).
    skillChips(s) {
      if (!s) return [];
      const chips = [];
      const live = s.status === "published" && s.published;
      if (live) {
        chips.push({
          kind: "live",
          label: `Live v${s.published.version}`,
          title: "This version is served to chat and MCP",
        });
      }
      const v = s.version || 1;
      if (live && v === s.published.version) return chips;
      if (s.reviewState === "in_review") {
        chips.push({
          kind: "in_review",
          label: `v${v} in review`,
          title: "Waiting for approval",
        });
      } else if (s.reviewState === "changes_requested") {
        chips.push({
          kind: "changes_requested",
          label: `v${v} changes requested`,
          title: "Sent back to the author — edit and save again",
        });
      } else if (s.reviewState === "approved") {
        // Unpublished: approved content exists but the skill is offline.
        chips.push({
          kind: "approved",
          label: `Offline (v${v} approved)`,
          title: "Taken offline — approve again to bring it back live",
        });
      } else {
        chips.push({
          kind: "draft",
          label: s.id ? `Draft v${v}` : "Draft",
          title: "Not sent for review yet",
        });
      }
      return chips;
    },
    decisionLabel(d) {
      return (
        {
          approved: "Approved",
          changes_requested: "Changes requested",
          pending: "Pending",
        }[d] || "Pending"
      );
    },
    fmtDate(ts) {
      return ts ? new Date(ts).toLocaleString() : "";
    },
    fmtRelative(ts) {
      if (!ts) return "—";
      const diff = Date.now() - ts;
      const h = 3600000;
      const d = 86400000;
      if (diff < h) return `${Math.max(1, Math.round(diff / 60000))}m ago`;
      if (diff < d) return `${Math.round(diff / h)}h ago`;
      if (diff < 30 * d) return `${Math.round(diff / d)}d ago`;
      return new Date(ts).toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    },
    trendHeight(uses) {
      const max = Math.max(1, ...this.usageTrend.map((d) => d.uses));
      return uses ? Math.max(6, Math.round((uses / max) * 100)) : 0;
    },
    userName(uid) {
      if (!uid) return "Unknown";
      if (uid === "seed-script") return "Seed";
      const u = this.users.find((x) => x.uid === uid);
      return (u && (u.name || u.email)) || uid;
    },
    syncFromStore(preferId) {
      this.working = (this.skills || []).map((s) => ({
        category: "",
        tags: [],
        status: "draft",
        reviewState: "draft",
        version: 1,
        currentVersion: 1,
        ...s,
      }));
      if (preferId) {
        const i = this.working.findIndex((s) => s.id === preferId);
        if (i >= 0) this.active = i;
      }
      if (this.active >= this.working.length) {
        this.active = Math.max(0, this.working.length - 1);
      }
    },
    selectSkill(i) {
      this.active = i;
      this.tab = "edit";
      this.previewVersion = null;
      if (this.categorySavedTimer) clearTimeout(this.categorySavedTimer);
      this.categorySaveState = "idle";
      const s = this.working[i];
      this.ownerSelection = (s && s.owner) || null;
      this.pendingFeedbackIds = [];
      this.aiSeed = null;
      this.aiSeedIds = [];
      this.diffOpen = {};
      if (s && s.id) this.loadReview(s.id);
      else {
        this.versions = [];
        this.comments = [];
        this.approvals = [];
        this.usageDaily = [];
        this.feedback = [];
      }
    },
    async loadApprovers() {
      const snap = await getDoc(doc(db, "admin", "skillApprovers"));
      this.approvers =
        snap.exists() && Array.isArray(snap.data().uids)
          ? snap.data().uids
          : [];
      this.approverSelection = [...this.approvers];
    },
    async saveOwner() {
      if (!this.current || !this.current.id) return;
      await this.callAction("setSkillOwner", { owner: this.ownerSelection });
    },
    async saveApprovers() {
      this.savingApprovers = true;
      try {
        const call = httpsCallable(functions, "setSkillApprovers");
        await call({ uids: this.approverSelection });
        await this.loadApprovers();
      } catch (e) {
        alert(e.message || "Failed to save approvers.");
      } finally {
        this.savingApprovers = false;
      }
    },
    async loadReview(id) {
      // Daily usage buckets are keyed by UTC day (see usageTrend).
      const since = new Date(Date.now() - 29 * 86400000)
        .toISOString()
        .slice(0, 10);
      const [vSnap, cSnap, aSnap, uSnap, fSnap] = await Promise.all([
        getDocs(
          query(
            collection(db, "skills", id, "versions"),
            orderBy("version", "desc")
          )
        ),
        getDocs(
          query(
            collection(db, "skills", id, "comments"),
            orderBy("createdAt", "asc")
          )
        ),
        getDocs(collection(db, "skills", id, "approvals")),
        getDocs(
          query(
            collection(db, "skillUsage", id, "dailyUses"),
            where("date", ">=", since)
          )
        ).catch((e) => {
          console.warn("skill usage buckets failed:", e);
          return { docs: [] };
        }),
        getDocs(collection(db, "skills", id, "feedback")).catch((e) => {
          console.warn("skill feedback failed:", e);
          return { docs: [] };
        }),
      ]);
      this.versions = vSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
      this.comments = cSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
      this.approvals = aSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
      this.usageDaily = uSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
      this.feedback = fSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
    },
    addSkill() {
      this.working.unshift({
        id: null,
        name: "",
        title: "",
        category: "",
        description: "",
        body: "",
        status: "draft",
        reviewState: "draft",
        version: 1,
        currentVersion: 1,
        tags: [],
      });
      this.active = 0;
      this.selectSkill(0);
    },
    async refresh(preferId) {
      await this.$store.dispatch("fetchSkills");
      this.syncFromStore(preferId);
      if (this.current && this.current.id)
        await this.loadReview(this.current.id);
    },
    // Category support was added after saveSkillVersion first shipped. During
    // a rolling deploy, an older callable succeeds but silently omits the new
    // field. Detect that response and backfill the top-level doc + the version
    // it just created; once the updated callable is live this is read-only.
    async ensureSavedCategory(skillId, version, category) {
      if (!skillId) return;
      const skillRef = doc(db, "skills", skillId);
      const saved = await getDoc(skillRef);
      if (saved.exists() && cleanCategory(saved.data().category) === category) {
        return;
      }

      await updateDoc(skillRef, { category });
      if (version == null) return;

      const versions = await getDocs(
        query(
          collection(db, "skills", skillId, "versions"),
          where("version", "==", version)
        )
      );
      await Promise.all(
        versions.docs.map((versionDoc) =>
          updateDoc(versionDoc.ref, { category })
        )
      );
    },
    async save() {
      if (!this.current) return;
      const v = validateSkill(this.current);
      if (!v.ok) {
        alert("Please fix:\n\n" + v.errors.join("\n"));
        return;
      }
      this.saving = true;
      try {
        const category = cleanCategory(this.current.category);
        const call = httpsCallable(functions, "saveSkillVersion");
        const res = await call({
          skillId: this.current.id || null,
          skill: {
            name: this.current.name.trim(),
            title: (this.current.title || "").trim(),
            category,
            description: (this.current.description || "").trim(),
            body: this.current.body || "",
            tags: this.current.tags || [],
          },
        });
        const savedId = res.data && res.data.skillId;
        await this.markFeedbackApplied(savedId, res.data && res.data.version);
        await this.ensureSavedCategory(
          savedId,
          res.data && res.data.version,
          category
        );
        await this.refresh(savedId);
        // A new draft lived at /skills/new; give it its own URL now.
        if (savedId && this.$route.params.id !== savedId) {
          this.$router.replace(`/skills/${savedId}`);
        }
      } catch (e) {
        alert(e.message || "Save failed.");
      } finally {
        this.saving = false;
      }
    },
    async removeSkill() {
      if (!this.current) return;
      const label = this.current.title || this.current.name || "this skill";
      if (!confirm(`Delete "${label}"? This cannot be undone.`)) return;
      if (this.current.id) {
        await deleteDoc(doc(db, "skills", this.current.id));
        await this.$store.dispatch("fetchSkills");
      }
      this.skipLeaveGuard = true;
      this.$router.push("/skills");
    },
    async callAction(name, extra) {
      if (!this.current || !this.current.id) return;
      this.acting = true;
      try {
        const call = httpsCallable(functions, name);
        await call({ skillId: this.current.id, ...(extra || {}) });
        await this.refresh(this.current.id);
      } catch (e) {
        alert(e.message || `${name} failed.`);
      } finally {
        this.acting = false;
      }
    },
    async review(decision) {
      await this.callAction("reviewSkill", {
        decision,
        comment: this.reviewComment || null,
      });
      this.reviewComment = "";
    },
    unpublish() {
      this.callAction("unpublishSkill");
    },
    async revert(ver) {
      if (!confirm(`Restore v${ver.version}? A new version will be created.`))
        return;
      await this.callAction("revertSkillVersion", { versionId: ver.id });
      this.tab = "edit";
      this.previewVersion = null;
    },
    async addComment() {
      const text = this.newComment.trim();
      if (!text || !this.current || !this.current.id) return;
      await addDoc(collection(db, "skills", this.current.id, "comments"), {
        authorUid: this.user?.uid || null,
        text,
        resolved: false,
        createdAt: Date.now(),
      });
      this.newComment = "";
      await this.loadReview(this.current.id);
    },
    async toggleResolve(c) {
      await updateDoc(doc(db, "skills", this.current.id, "comments", c.id), {
        resolved: !c.resolved,
      });
      await this.loadReview(this.current.id);
    },
    feedbackAuthor(f) {
      const known =
        f.authorUid && this.users.find((u) => u.uid === f.authorUid);
      return (
        (known && (known.name || known.email)) ||
        f.authorName ||
        f.authorEmail ||
        "A colleague"
      );
    },
    resolutionLabel(f) {
      return resolutionLabel(f);
    },
    // A proposal against the live version it was written for.
    proposalDiff(f) {
      const s = this.current;
      const live = (s && s.published && s.published.body) || (s && s.body);
      return foldDiff(lineDiff(live, f.proposed && f.proposed.body));
    },
    // Load a proposed revision into the editor. Saving puts it in review.
    applyProposal(f) {
      const s = this.current;
      if (!s || !f.proposed) return;
      const live = (s.published && s.published.body) || "";
      if (
        (s.body || "") !== live &&
        !confirm(
          "The editor holds changes that aren't live yet. Replace its text with this proposal?"
        )
      ) {
        return;
      }
      s.body = f.proposed.body;
      if (f.proposed.description) s.description = f.proposed.description;
      this.pendingFeedbackIds = [
        ...new Set([...this.pendingFeedbackIds, f.id]),
      ];
      this.tab = "edit";
    },
    // Hand every open item to the AI drawer, which drafts one revision.
    processFeedbackWithAI() {
      const open = this.feedbackSplit.open;
      if (!open.length) return;
      this.aiSeed = { key: Date.now(), text: feedbackPrompt(open) };
      this.aiSeedIds = open.map((f) => f.id);
      this.showChat = true;
    },
    async resolveFeedback(ids, status, version) {
      if (!this.current || !this.current.id || !ids.length) return;
      this.acting = true;
      try {
        const call = httpsCallable(functions, "resolveSkillFeedback");
        await call({ skillId: this.current.id, ids, status, version });
        await this.loadReview(this.current.id);
      } catch (e) {
        alert(e.message || "Could not update the feedback.");
      } finally {
        this.acting = false;
      }
    },
    // After a save: the applied feedback went into this version. A failure
    // here must not fail the save, so the items just stay open.
    async markFeedbackApplied(skillId, version) {
      const ids = this.pendingFeedbackIds;
      this.pendingFeedbackIds = [];
      if (!skillId || !ids.length) return;
      try {
        const call = httpsCallable(functions, "resolveSkillFeedback");
        await call({ skillId, ids, status: "applied", version });
      } catch (e) {
        console.warn("marking feedback applied failed:", e);
      }
    },
    // The AI authoring chat emitted a finished skill draft. The drawer was
    // handed the skill on screen, so its answer replaces that working copy;
    // nothing is saved or sent for review until Save. A saved skill keeps its
    // name: MCP clients and the kennisbank link to it.
    onAuthored(skill) {
      if (!this.current) return;
      const s = this.current;
      if (!s.id) s.name = skill.name || s.name;
      s.title = skill.title || s.title;
      s.category = cleanCategory(skill.category) || s.category;
      s.description = skill.description || s.description;
      s.body = skill.body || s.body;
      // A draft written from "Process with AI" covers that feedback.
      if (this.aiSeedIds.length) {
        this.pendingFeedbackIds = [
          ...new Set([...this.pendingFeedbackIds, ...this.aiSeedIds]),
        ];
        this.aiSeedIds = [];
      }
      this.tab = "edit";
    },
  },
};
</script>

<style scoped lang="scss">
.alice-shell {
  display: grid;
  grid-template-rows: 56px 1fr;
  grid-template-columns: 56px 1fr;
  height: 100vh;
  width: 100%;
  background: var(--bg-muted);
  font-family: var(--font-body);
  color: var(--fg-1);
  overflow: hidden;
}
.alice-shell__topbar {
  grid-column: 1 / -1;
  grid-row: 1;
}
.alice-shell__sidebar {
  grid-column: 1;
  grid-row: 2;
}
.alice-shell__main {
  grid-column: 2;
  grid-row: 2;
  display: flex;
  flex-direction: column;
  min-width: 0;
  overflow: hidden;
  background: var(--vp-white);
}

/* Top bar breadcrumb: Skills › <this skill> */
.alice-tb-crumb {
  text-decoration: none;
  color: var(--vp-grijs-80);
  white-space: nowrap;
}
.alice-tb-crumb:hover {
  color: var(--vp-white);
}
.alice-tb-crumb--current {
  color: var(--vp-white);
  text-transform: none;
  letter-spacing: 0.04em;
  overflow: hidden;
  text-overflow: ellipsis;
}
.alice-tb-sep {
  color: var(--vp-grijs-160);
  font-weight: 400;
}

/* State + badges */
.alice-skills__state {
  display: inline-block;
  width: fit-content;
  font-family: var(--font-ui);
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  padding: 2px 6px;
  background: var(--bg-muted);
  color: var(--fg-3);
}
.alice-skills__state--in_review {
  background: #fff4d6;
  color: #7a5b00;
}
.alice-skills__state--changes_requested {
  background: #fde2e1;
  color: var(--vp-rood-100);
}
.alice-skills__state--approved {
  background: var(--accent-20);
  color: var(--vp-black);
}
.alice-skills__state--live {
  background: var(--vp-black);
  color: var(--vp-white);
}

/* ===== Editor ===== */
.alice-skills__main {
  overflow-y: auto;
}
.alice-skills__empty {
  padding: 48px 32px;
  color: var(--fg-3);
  font-size: 15px;
}
.alice-skills__editor {
  display: flex;
  flex-direction: column;
  min-height: 0;
  flex: 1;
  // Full width now that the skill list is its own page: keep lines readable.
  width: 100%;
  max-width: 1120px;
  margin: 0 auto;
}
.alice-skills__editor-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  padding: 24px 32px 16px;
}
.alice-skills__editor-heading {
  min-width: 0;
}
.alice-skills__editor-title {
  margin: 0 0 8px;
  font-family: var(--font-display);
  font-size: 28px;
  font-weight: 700;
  letter-spacing: -0.02em;
  color: var(--vp-black);
  overflow: hidden;
  text-overflow: ellipsis;
}
.alice-skills__editor-meta {
  display: flex;
  align-items: center;
  gap: 8px;
}
.alice-skills__vtag {
  font-family: var(--font-ui);
  font-size: 11px;
  font-weight: 700;
  color: var(--fg-3);
}
.alice-skills__editor-actions {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;
}
.alice-skills__ghost {
  height: 38px;
  padding: 0 16px;
  background: var(--vp-white);
  color: var(--vp-black);
  border: 1px solid var(--border-hairline);
  cursor: pointer;
  font-family: var(--font-ui);
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
}
.alice-skills__ghost:hover:not(:disabled) {
  background: var(--bg-muted);
}
.alice-skills__ghost:disabled {
  opacity: 0.4;
  cursor: default;
}
.alice-skills__ghost--icon {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}
.alice-skills__ghost--on,
.alice-skills__ghost--on:hover:not(:disabled) {
  background: var(--vp-black);
  border-color: var(--vp-black);
  color: var(--vp-white);
}
.alice-skills__delete {
  width: 38px;
  height: 38px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: 1px solid var(--border-hairline);
  cursor: pointer;
  color: var(--fg-3);
}
.alice-skills__delete:hover {
  color: var(--vp-rood-100);
  border-color: var(--vp-rood-100);
}
.alice-skills__save {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  height: 38px;
  padding: 0 20px;
  background: var(--vp-black);
  color: var(--vp-white);
  border: none;
  cursor: pointer;
  font-family: var(--font-ui);
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}
.alice-skills__save:hover:not(:disabled) {
  background: var(--accent-100);
  color: var(--vp-black);
}
.alice-skills__save:disabled {
  opacity: 0.5;
  cursor: default;
}
.alice-skills__save--sm {
  height: 34px;
  padding: 0 14px;
  font-size: 12px;
}

/* Tabs */
.alice-skills__tabs {
  display: flex;
  gap: 0;
  padding: 0 32px;
  border-bottom: 1px solid var(--border-hairline);
}
.alice-skills__tab {
  height: 44px;
  padding: 0 16px;
  background: transparent;
  border: none;
  border-bottom: 2px solid transparent;
  cursor: pointer;
  font-family: var(--font-ui);
  font-size: 13px;
  font-weight: 600;
  color: var(--fg-3);
}
.alice-skills__tab:hover:not(:disabled) {
  color: var(--vp-black);
}
.alice-skills__tab--active {
  color: var(--vp-black);
  border-bottom-color: var(--accent-100);
  font-weight: 700;
}
.alice-skills__tab:disabled {
  opacity: 0.4;
  cursor: default;
}
.alice-skills__tab-count {
  margin-left: 6px;
  font-size: 11px;
  color: var(--fg-3);
}

.alice-skills__fields {
  padding: 20px 32px 32px;
  display: flex;
  flex-direction: column;
  gap: 18px;
}
.alice-skills__field {
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.alice-skills__label {
  font-family: var(--font-ui);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--fg-3);
}
.alice-skills__label em {
  font-style: normal;
  text-transform: none;
  letter-spacing: 0;
  font-weight: 500;
  color: var(--fg-3);
}
.alice-skills__field input,
.alice-skills__field textarea {
  border: 1px solid var(--border-hairline);
  outline: none;
  padding: 10px 12px;
  font-family: var(--font-body);
  font-size: 14px;
  line-height: 1.55;
  color: var(--vp-black);
  background: var(--vp-white);
}
.alice-skills__field input:focus,
.alice-skills__field textarea:focus {
  border-color: var(--vp-black);
}
.alice-skills__hint {
  margin: 0;
  font-family: var(--font-ui);
  font-size: 12px;
  color: var(--fg-3);
}
.alice-skills__field-hint {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-family: var(--font-ui);
  font-size: 11px;
  color: var(--fg-3);
  text-transform: none;
  letter-spacing: 0;
}

/* Loading + saving spinners (thin monochrome ring, inherits currentColor) */
.alice-skills__loading {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 48px 0;
  color: var(--fg-3);
}
.alice-skills__spinner {
  display: inline-block;
  width: 14px;
  height: 14px;
  border: 2px solid currentColor;
  border-right-color: transparent;
  border-radius: 50%;
  animation: alice-skills-spin 0.6s linear infinite;
}
.alice-skills__spinner--sm {
  width: 10px;
  height: 10px;
  border-width: 1.5px;
}
.alice-skills__spinner--lg {
  width: 26px;
  height: 26px;
  border-width: 3px;
}
@keyframes alice-skills-spin {
  to {
    transform: rotate(360deg);
  }
}
@media (prefers-reduced-motion: reduce) {
  .alice-skills__spinner {
    animation-duration: 1.2s;
  }
}

.alice-skills__validation {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 12px 14px;
  background: var(--bg-muted);
}
.alice-skills__v {
  display: flex;
  align-items: center;
  gap: 8px;
  font-family: var(--font-ui);
  font-size: 12.5px;
}
.alice-skills__v--error {
  color: var(--vp-rood-100);
}
.alice-skills__v--warn {
  color: var(--fg-2);
}

/* Usage tab */
.alice-skills__usage-kpis {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 12px;
}
.alice-skills__usage-kpi {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 14px 16px;
  border: 1px solid var(--border-hairline);
}
.alice-skills__usage-kpi-value {
  font-family: var(--font-display);
  font-size: 26px;
  line-height: 1.1;
  color: var(--vp-black);
}
.alice-skills__usage-kpi-value--sm {
  font-size: 16px;
  padding-top: 5px;
}
.alice-skills__usage-kpi-label {
  font-family: var(--font-ui);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--fg-3);
}
.alice-skills__usage-sources {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  font-family: var(--font-ui);
  font-size: 13px;
  color: var(--fg-3);
}
.alice-skills__usage-sources strong {
  color: var(--vp-black);
}
.alice-skills__usage-trend-label {
  margin-bottom: 8px;
  font-family: var(--font-ui);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--fg-3);
}
.alice-skills__usage-trend {
  display: flex;
  align-items: flex-end;
  gap: 3px;
  height: 64px;
  padding: 0 0 1px;
  border-bottom: 1px solid var(--border-hairline);
}
.alice-skills__usage-bar {
  flex: 1 1 0;
  display: flex;
  align-items: flex-end;
  height: 100%;
  background: var(--bg-muted);
}
.alice-skills__usage-fill {
  display: block;
  width: 100%;
  background: var(--accent-100);
}
@media (max-width: 900px) {
  .alice-skills__usage-kpis {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

/* Panes (versions / reviewers / comments) */
.alice-skills__pane {
  padding: 20px 32px 32px;
  display: flex;
  flex-direction: column;
  gap: 14px;
}
.alice-skills__pane-empty {
  color: var(--fg-3);
  font-size: 14px;
}
.alice-skills__section-title {
  font-family: var(--font-ui);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--fg-3);
}
.alice-skills__section-title em {
  font-style: normal;
  text-transform: none;
  letter-spacing: 0;
  font-weight: 500;
}
.alice-skills__chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}
.alice-skills__chip {
  font-family: var(--font-ui);
  font-size: 12px;
  font-weight: 600;
  padding: 3px 10px;
  background: var(--bg-muted);
  color: var(--vp-black);
  border: 1px solid var(--border-hairline);
}
.alice-skills__inline-edit {
  display: flex;
  align-items: center;
  gap: 10px;
}
.alice-skills__select {
  height: 34px;
  padding: 0 10px;
  border: 1px solid var(--border-hairline);
  background: var(--vp-white);
  font-family: var(--font-body);
  font-size: 14px;
  min-width: 220px;
}
.alice-skills__linkbtn {
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  font-family: var(--font-ui);
  font-size: 12px;
  font-weight: 700;
  color: var(--vp-black);
  text-decoration: underline;
}
.alice-skills__linkbtn:disabled {
  opacity: 0.4;
  cursor: default;
}

/* Versions */
.alice-skills__versions {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
}
.alice-skills__version {
  padding: 12px 0;
  border-bottom: 1px solid var(--border-hairline);
}
.alice-skills__version-main {
  display: flex;
  align-items: center;
  gap: 10px;
}
.alice-skills__version-current {
  font-family: var(--font-ui);
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  padding: 1px 6px;
  background: var(--accent-20);
}
.alice-skills__version-msg {
  font-size: 14px;
  color: var(--vp-black);
}
.alice-skills__version-sub {
  margin-top: 2px;
  font-size: 12px;
  color: var(--fg-3);
}
.alice-skills__version-actions {
  margin-top: 6px;
  display: flex;
  gap: 16px;
}
.alice-skills__preview {
  border: 1px solid var(--border-hairline);
  background: var(--bg-muted);
}
.alice-skills__preview-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 12px;
  border-bottom: 1px solid var(--border-hairline);
  font-family: var(--font-ui);
  font-size: 12px;
  font-weight: 700;
}
.alice-skills__preview-body {
  margin: 0;
  padding: 12px;
  max-height: 320px;
  overflow: auto;
  font-family: var(--font-mono);
  font-size: 12.5px;
  white-space: pre-wrap;
  word-break: break-word;
}

/* Reviewers */
.alice-skills__reviewers {
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.alice-skills__reviewer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px solid var(--border-hairline);
  font-size: 14px;
}
.alice-skills__decision {
  font-family: var(--font-ui);
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  padding: 2px 6px;
  background: var(--bg-muted);
  color: var(--fg-3);
}
.alice-skills__decision--approved {
  background: var(--accent-20);
  color: var(--vp-black);
}
.alice-skills__decision--changes_requested {
  background: #fde2e1;
  color: var(--vp-rood-100);
}
.alice-skills__assign summary {
  cursor: pointer;
  font-family: var(--font-ui);
  font-size: 13px;
  font-weight: 700;
  padding: 6px 0;
}
.alice-skills__assign-list {
  display: flex;
  flex-direction: column;
  gap: 6px;
  max-height: 220px;
  overflow-y: auto;
  padding: 8px 0;
}
.alice-skills__assign-row {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
}
.alice-skills__review-box {
  margin-top: 8px;
  padding: 14px;
  border: 1px solid var(--border-hairline);
  background: var(--bg-muted);
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.alice-skills__review-box textarea {
  border: 1px solid var(--border-hairline);
  padding: 8px 10px;
  font-family: var(--font-body);
  font-size: 14px;
  outline: none;
}
.alice-skills__review-actions {
  display: flex;
  gap: 10px;
}
.alice-skills__approve,
.alice-skills__request {
  height: 34px;
  padding: 0 14px;
  border: none;
  cursor: pointer;
  font-family: var(--font-ui);
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
}
.alice-skills__approve {
  background: var(--vp-black);
  color: var(--vp-white);
}
.alice-skills__request {
  background: var(--vp-white);
  color: var(--vp-rood-100);
  border: 1px solid var(--vp-rood-100);
}
.alice-skills__approve:disabled,
.alice-skills__request:disabled {
  opacity: 0.5;
  cursor: default;
}

/* Comments */
.alice-skills__comments {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.alice-skills__comment {
  border-bottom: 1px solid var(--border-hairline);
  padding-bottom: 12px;
}
.alice-skills__comment--resolved {
  opacity: 0.55;
}
.alice-skills__comment-head {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 4px;
}
.alice-skills__comment-author {
  font-family: var(--font-ui);
  font-size: 12px;
  font-weight: 700;
  color: var(--vp-black);
}
.alice-skills__comment-date {
  font-size: 11px;
  color: var(--fg-3);
  flex: 1;
}
.alice-skills__comment-body {
  font-size: 14px;
  color: var(--vp-black);
  white-space: pre-wrap;
}
.alice-skills__comment-form {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 4px;
}
.alice-skills__comment-form textarea {
  border: 1px solid var(--border-hairline);
  padding: 8px 10px;
  font-family: var(--font-body);
  font-size: 14px;
  outline: none;
}
.alice-skills__comment-form button {
  align-self: flex-start;
}

/* Feedback */
.alice-skills__applied {
  padding: 10px 14px;
  background: var(--vp-blauw-10);
  border-left: 3px solid var(--vp-blauw-100);
  font-size: 14px;
  color: var(--vp-black);
}
.alice-skills__fb-intro {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
}
.alice-skills__fb-intro .alice-skills__hint {
  margin: 0;
  max-width: 640px;
}
.alice-skills__fb-intro .alice-skills__ghost {
  flex-shrink: 0;
}
.alice-skills__fbs {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
}
.alice-skills__fb {
  padding: 14px 0;
  border-bottom: 1px solid var(--border-hairline);
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.alice-skills__fb--handled {
  opacity: 0.6;
}
.alice-skills__fb-head {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}
.alice-skills__fb-kind,
.alice-skills__fb-rating,
.alice-skills__fb-resolution {
  font-family: var(--font-ui);
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  padding: 2px 6px;
  background: var(--bg-muted);
  color: var(--fg-3);
}
.alice-skills__fb-kind--change_request {
  background: var(--vp-geel-10);
  color: var(--vp-geel-160);
}
.alice-skills__fb-kind--update {
  background: var(--vp-blauw-10);
  color: var(--vp-blauw-160);
}
.alice-skills__fb-rating--worked {
  background: var(--vp-groen-10);
  color: var(--vp-groen-160);
}
.alice-skills__fb-rating--partly {
  background: var(--vp-geel-10);
  color: var(--vp-geel-160);
}
.alice-skills__fb-rating--did_not_work {
  background: var(--vp-rood-10);
  color: var(--vp-rood-100);
}
.alice-skills__fb-resolution {
  background: var(--vp-white);
  box-shadow: inset 0 0 0 1px var(--border-hairline);
}
.alice-skills__fb-summary {
  font-size: 15px;
  font-weight: 600;
  color: var(--vp-black);
}
.alice-skills__fb-details {
  font-size: 14px;
  color: var(--vp-black);
  white-space: pre-wrap;
}
.alice-skills__fb-meta {
  font-size: 12px;
  color: var(--fg-3);
}
.alice-skills__fb-actions {
  display: flex;
  gap: 16px;
  margin-top: 2px;
}
.alice-skills__fb-more {
  align-self: flex-start;
}
.alice-skills__diff {
  margin-top: 8px;
  border: 1px solid var(--border-hairline);
  background: var(--vp-white);
  max-height: 360px;
  overflow: auto;
  font-family: var(--font-mono);
  font-size: 12.5px;
}
.alice-skills__diff-desc {
  padding: 8px 12px;
  border-bottom: 1px solid var(--border-hairline);
  font-family: var(--font-body);
  font-size: 13px;
}
.alice-skills__diff-row {
  padding: 1px 12px 1px 24px;
  position: relative;
  white-space: pre-wrap;
  word-break: break-word;
}
.alice-skills__diff-row--add {
  background: var(--vp-groen-10);
}
.alice-skills__diff-row--add::before {
  content: "+";
  position: absolute;
  left: 8px;
}
.alice-skills__diff-row--del {
  background: var(--vp-rood-10);
  text-decoration: line-through;
  text-decoration-color: rgba(0, 0, 0, 0.3);
}
.alice-skills__diff-row--del::before {
  content: "−";
  position: absolute;
  left: 8px;
}
.alice-skills__diff-row--skip {
  color: var(--fg-3);
  font-family: var(--font-ui);
  font-size: 11px;
  padding-top: 4px;
  padding-bottom: 4px;
  background: var(--bg-muted);
}

/* ===== AI drawer ===== */
.alice-skills__drawer {
  position: fixed;
  top: 56px;
  right: 0;
  bottom: 0;
  width: 440px;
  max-width: 90vw;
  z-index: 40;
  box-shadow: -8px 0 24px rgba(0, 0, 0, 0.08);
}
</style>
